import { clockOf, minutesOf, WEEKDAY_NAMES } from "./agenda-view.js";
import { addDays, formatTimeOfDay, isoWeekdayOf, timeOf, todayIn } from "./period.js";
/** The ISO weekday's name in plural, for «no atiende los domingos». */
const WEEKDAY_PLURALS = {
    1: "lunes", 2: "martes", 3: "miércoles", 4: "jueves", 5: "viernes", 6: "sábados", 7: "domingos",
};
function rangesOn(hours, day) {
    const weekday = isoWeekdayOf(day);
    return hours.filter((h) => h.weekday === weekday);
}
/** A sentence's closing dot, never a second one after «p.m.». */
function closed(sentence) {
    return sentence.endsWith(".") ? sentence : `${sentence}.`;
}
/** «de 8:00 a.m. a 5:00 p.m.», several joined with « y ». */
function rangesSentence(ranges) {
    return ranges.map((r) => `de ${formatTimeOfDay(r.opens)} a ${formatTimeOfDay(r.closes)}`).join(" y ");
}
/** Whether `time` falls in the range: `opens` in, `closes` out. */
function holds(range, time) {
    return range.opens <= time && time < range.closes;
}
/** Whether the business attends on `day` at `time`. Without a time, an open day is enough; without hours, always. */
export function attendsAt(hours, day, time) {
    if (hours.length === 0)
        return true;
    const ranges = rangesOn(hours, day);
    if (time === null)
        return ranges.length > 0;
    return ranges.some((r) => holds(r, time));
}
/** Why the business does not attend then, in its words, or null when it does. */
export function hoursRefusal(hours, day, time, businessName) {
    if (attendsAt(hours, day, time))
        return null;
    const ranges = rangesOn(hours, day);
    if (ranges.length === 0)
        return `${businessName} no atiende los ${WEEKDAY_PLURALS[isoWeekdayOf(day)] ?? ""}.`;
    return closed(`Ese día atiende ${rangesSentence(ranges)}`);
}
/** «Atiende de 8:00 a.m. a 5:00 p.m.» under the day picked; null without hours or on a closed day. */
export function dayHoursLine(hours, day) {
    const ranges = rangesOn(hours, day);
    return ranges.length === 0 ? null : closed(`Atiende ${rangesSentence(ranges)}`);
}
export function requestNowIn(tz, now) {
    return { today: todayIn(tz, now), now: timeOf(now.toISOString(), tz) };
}
/** «a las 8:00 a.m.», «a la 1:00 p.m.». */
function atTime(time) {
    const clock = formatTimeOfDay(time);
    return clock.startsWith("1:") ? `a la ${clock}` : `a las ${clock}`;
}
/**
 * WHETHER IT IS OPEN NOW, on the business's wall clock -- never the
 * visitor's: someone reading from abroad sees Panama's hours. Null for a
 * business that gave no hours (nothing is said, as the hours card).
 */
export function todayHoursOf(hours, timezone, now) {
    if (hours.length === 0)
        return null;
    const at = requestNowIn(timezone, now);
    const on = (offset) => rangesOn(hours, addDays(at.today, offset)).sort((a, b) => a.opens.localeCompare(b.opens));
    const today = on(0);
    const current = today.find((range) => holds(range, at.now));
    if (current !== undefined)
        return { open: true, state: "Abierto ahora", detail: `Cierra ${atTime(current.closes)}` };
    const later = today.find((range) => range.opens > at.now);
    if (later !== undefined)
        return { open: false, state: "Cerrado ahora", detail: `Abre hoy ${atTime(later.opens)}` };
    for (let offset = 1; offset <= 7; offset += 1) {
        const [first] = on(offset);
        if (first === undefined)
            continue;
        const day = offset === 1 ? "mañana" : `el ${(WEEKDAY_NAMES[isoWeekdayOf(addDays(at.today, offset))] ?? "").toLowerCase()}`;
        return { open: false, state: "Cerrado ahora", detail: `Abre ${day} ${atTime(first.opens)}` };
    }
    return null;
}
const SLOT_MINUTES = 30;
const ALL_DAY = { weekday: 0, opens: "00:00", closes: "24:00" };
/**
 * The hours offered on `day`: every half hour on the :00/:30 grid, from the
 * opening rounded up while before closing. Without hours, the whole day's 48.
 * Today, only those after now.
 */
export function requestSlotsOf(hours, day, at) {
    const ranges = hours.length === 0 ? [ALL_DAY] : rangesOn(hours, day);
    const slots = [];
    for (const range of ranges) {
        const first = Math.ceil(minutesOf(range.opens) / SLOT_MINUTES) * SLOT_MINUTES;
        for (let m = first; m < minutesOf(range.closes); m += SLOT_MINUTES) {
            const slot = clockOf(m);
            if (day !== at.today || slot > at.now)
                slots.push(slot);
        }
    }
    return slots.sort();
}
export const REQUEST_DAYS_AHEAD = 60;
/**
 * The days offered, from today: those the business attends. Today only while
 * an hour is left, except without hours, where «Sin hora» keeps any day open.
 */
export function requestDaysOf(hours, at, ahead = REQUEST_DAYS_AHEAD) {
    const days = [];
    for (let i = 0; i < ahead; i += 1) {
        const day = addDays(at.today, i);
        if (hours.length === 0 || requestSlotsOf(hours, day, at).length > 0)
            days.push(day);
    }
    return days;
}
/**
 * What the request form shows for her choice: the days
 * offered, her day while still offered -- else the first --, that day's
 * hours, and her hour while that day has it -- else «Sin hora». A choice time
 * overtook while the form stayed open falls back the same way. Both clients
 * call it again with the new day when she changes it: the hour it gives back
 * is the one that survives the change.
 */
export function requestChoiceOf(hours, at, chosen) {
    const days = requestDaysOf(hours, at);
    const day = chosen.day !== null && days.includes(chosen.day) ? chosen.day : (days[0] ?? at.today);
    const slots = requestSlotsOf(hours, day, at);
    return { days, day, slots, time: chosen.time !== null && slots.includes(chosen.time) ? chosen.time : null };
}
function daysLabel(first, last) {
    const from = WEEKDAY_NAMES[first] ?? "";
    if (first === last)
        return from;
    const to = (WEEKDAY_NAMES[last] ?? "").toLowerCase();
    return last === first + 1 ? `${from} y ${to}` : `${from} a ${to}`;
}
/**
 * The week as the customer reads it in «Con quién trabajo»: days in a row with
 * the same hours together («Lunes a viernes» / «8:00 a.m. – 5:00 p.m.»), a
 * closed day «Cerrado» and flagged, so a screen draws it faint without
 * comparing words. Without hours, nothing.
 */
export function weekHoursSummary(hours) {
    if (hours.length === 0)
        return [];
    const lineOf = (weekday) => {
        const ranges = hours.filter((h) => h.weekday === weekday).sort((a, b) => a.opens.localeCompare(b.opens));
        if (ranges.length === 0)
            return null;
        return ranges.map((r) => `${formatTimeOfDay(r.opens)} – ${formatTimeOfDay(r.closes)}`).join(" y ");
    };
    const groups = [];
    for (let weekday = 1; weekday <= 7; weekday += 1) {
        const line = lineOf(weekday);
        const previous = groups[groups.length - 1];
        if (previous !== undefined && previous.hours === line)
            previous.last = weekday;
        else
            groups.push({ first: weekday, last: weekday, hours: line });
    }
    return groups.map((g) => ({ days: daysLabel(g.first, g.last), hours: g.hours ?? "Cerrado", closed: g.hours === null }));
}
